/**
 * ToastContext
 *
 * Context and components for toast notifications.
 * Provides transient error/success/info messages that auto-dismiss.
 *
 * Phase 4: Error handling and display
 */

import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";
import React, { createContext, useCallback, useContext, useState } from "react";

// ============================================================================
// Types
// ============================================================================

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  /** Duration in ms before auto-dismiss. 0 = persistent */
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  // Convenience methods
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
}

// ============================================================================
// Context
// ============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// ============================================================================
// Provider
// ============================================================================

interface ToastProviderProps {
  children: React.ReactNode;
  /** Maximum number of toasts to show at once */
  maxToasts?: number;
  /** Default duration for toasts (ms) */
  defaultDuration?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
  defaultDuration = 5000,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, "id">): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const duration = toast.duration ?? defaultDuration;

      setToasts((prev) => {
        // Remove oldest if at max
        const next = prev.length >= maxToasts ? prev.slice(1) : prev;
        return [...next, { ...toast, id, duration }];
      });

      // Auto-dismiss if duration > 0
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [defaultDuration, maxToasts, removeToast]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback(
    (message: string, duration?: number) => addToast({ type: "success", message, duration }),
    [addToast]
  );

  const error = useCallback(
    (message: string, duration?: number) => addToast({ type: "error", message, duration: duration ?? 8000 }),
    [addToast]
  );

  const warning = useCallback(
    (message: string, duration?: number) => addToast({ type: "warning", message, duration: duration ?? 6000 }),
    [addToast]
  );

  const info = useCallback(
    (message: string, duration?: number) => addToast({ type: "info", message, duration }),
    [addToast]
  );

  const value: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// ============================================================================
// Toast Container & Item Components
// ============================================================================

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const config = getToastConfig(toast.type);
  const Icon = config.icon;

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 p-4 rounded-lg shadow-lg border backdrop-blur-sm animate-slide-in-right ${config.containerClasses}`}
      role="alert"
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${config.iconClasses}`} />
      <p className={`text-sm flex-1 ${config.textClasses}`}>{toast.message}</p>
      <button
        onClick={() => onRemove(toast.id)}
        className={`flex-shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors ${config.textClasses}`}
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ============================================================================
// Helpers
// ============================================================================

interface ToastConfig {
  icon: typeof CheckCircle;
  containerClasses: string;
  iconClasses: string;
  textClasses: string;
}

function getToastConfig(type: ToastType): ToastConfig {
  switch (type) {
    case "success":
      return {
        icon: CheckCircle,
        containerClasses:
          "bg-green-50 dark:bg-green-900/90 border-green-200 dark:border-green-800",
        iconClasses: "text-green-600 dark:text-green-400",
        textClasses: "text-green-800 dark:text-green-200",
      };
    case "error":
      return {
        icon: XCircle,
        containerClasses:
          "bg-red-50 dark:bg-red-900/90 border-red-200 dark:border-red-800",
        iconClasses: "text-red-600 dark:text-red-400",
        textClasses: "text-red-800 dark:text-red-200",
      };
    case "warning":
      return {
        icon: AlertTriangle,
        containerClasses:
          "bg-yellow-50 dark:bg-yellow-900/90 border-yellow-200 dark:border-yellow-800",
        iconClasses: "text-yellow-600 dark:text-yellow-400",
        textClasses: "text-yellow-800 dark:text-yellow-200",
      };
    case "info":
    default:
      return {
        icon: Info,
        containerClasses:
          "bg-blue-50 dark:bg-blue-900/90 border-blue-200 dark:border-blue-800",
        iconClasses: "text-blue-600 dark:text-blue-400",
        textClasses: "text-blue-800 dark:text-blue-200",
      };
  }
}

export default ToastContext;

